import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, LogIn, Phone, Fingerprint } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useBranding, brandingAssetUrl } from '../context/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { passkeysSupported, platformAuthenticatorAvailable, checkHasPasskey } from '../lib/passkey';

export default function LoginPage() {
  const { login, loginWithPasskey } = useAuth();
  const { branding } = useBranding();
  const [params] = useSearchParams();
  const inviteCode = params.get('invite');
  const redirectTo = params.get('redirect'); // iter125 — preserve /s/:encoded landing
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // iter195 — "Keep me logged in for 30 days". Defaults to ON for personal devices
  // (skip re-auth weekly). User can untick for shared / kiosk machines.
  const [rememberMe, setRememberMe] = useState(() => {
    try {
      const saved = localStorage.getItem('pref_remember_me');
      return saved === null ? true : saved === 'true';
    } catch {
      return true;
    }
  });
  const [loading, setLoading] = useState(false);
  // iter195 (Phase 3) — Passkey "Sign in with Face ID" eligibility check.
  // We show the button only when (a) the device has a platform authenticator,
  // (b) WebAuthn is available, AND (c) the phone-number entered has a passkey
  // registered server-side. Local hint comes from `pref_last_passkey_phone`
  // so the button surfaces immediately on the second visit.
  const [passkeyReady, setPasskeyReady] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!passkeysSupported()) return;
      const platformOk = await platformAuthenticatorAvailable();
      if (cancelled) return;
      if (!platformOk) return;
      // Pre-fill phone from the last successful passkey unlock if no phone typed.
      let lookupPhone = phone;
      if (!lookupPhone) {
        try { lookupPhone = localStorage.getItem('pref_last_passkey_phone') || ''; } catch { /* ignore */ }
      }
      if (!lookupPhone) {
        // No phone to check → still let the user discover passkeys via the button
        // (discoverable credentials). Mark ready so the button shows.
        if (!cancelled) setPasskeyReady(true);
        return;
      }
      const hasOne = await checkHasPasskey(lookupPhone);
      if (!cancelled) setPasskeyReady(hasOne);
    })();
    return () => { cancelled = true; };
  }, [phone]);

  const handlePasskeyLogin = async () => {
    if (passkeyBusy) return;
    setPasskeyBusy(true);
    try {
      try { localStorage.setItem('pref_remember_me', String(rememberMe)); } catch { /* ignore */ }
      const userData = await loginWithPasskey({
        phone: phone || undefined,
        rememberMe,
      });
      if (userData?.phone) {
        try { localStorage.setItem('pref_last_passkey_phone', userData.phone); } catch { /* ignore */ }
      }
      if (inviteCode) {
        try { localStorage.setItem('pending_invite_code', inviteCode); } catch { /* ignore */ }
      }
      toast.success('Signed in with Face ID');
      // App router will redirect once `user` is set.
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Face ID sign-in failed';
      toast.error(detail);
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      try { localStorage.setItem('pref_remember_me', String(rememberMe)); } catch { /* ignore */ }
      await login(phone, password, rememberMe);
      // iter195.5 — signal the EnableFaceIDPrompt to consider showing after redirect.
      try { sessionStorage.setItem('just_logged_in_password', 'true'); } catch { /* ignore */ }
      // Honor invite link from /welcome?invite=CODE → /login?invite=CODE flow
      if (inviteCode) {
        try { localStorage.setItem('pending_invite_code', inviteCode); } catch { /* ignore */ }
      }
      toast.success('Welcome back!');
      // iter125 — after login, bounce back to the original /s/<encoded> landing
      // so the settle flow can resume right where it left off.
      let target = redirectTo;
      if (!target) {
        try { target = localStorage.getItem('pending_redirect_after_login'); } catch { target = null; }
        try { localStorage.removeItem('pending_redirect_after_login'); } catch { /* ignore */ }
      }
      if (target && target.startsWith('/')) {
        window.location.replace(target);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Hero Section */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Logo */}
          <div className="mb-8">
            {branding.logo_url && (
              <img
                src={brandingAssetUrl(branding.logo_url)}
                alt={branding.app_name}
                className="w-14 h-14 rounded-2xl object-contain mb-3"
                data-testid="login-brand-logo"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <h1 className="font-display text-4xl font-bold tracking-tight" data-testid="login-brand-name">
              {branding.app_name || 'SplitEasy'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {branding.tagline || 'Track expenses with friends'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-semibold uppercase tracking-wide">
                Phone Number
              </Label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="username"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="9820030046"
                  data-testid="login-phone-input"
                  className="h-14 text-lg border-2 rounded-xl pl-12"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold uppercase tracking-wide">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  data-testid="login-password-input"
                  className="h-14 text-lg border-2 rounded-xl pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* iter195 — Keep me logged in for 30 days */}
            <label
              className="flex items-center gap-3 cursor-pointer select-none"
              data-testid="remember-me-label"
            >
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                data-testid="remember-me-checkbox"
                className="w-5 h-5 rounded border-2 border-border accent-primary cursor-pointer"
              />
              <span className="text-sm font-semibold text-foreground">
                Keep me logged in for 30 days
              </span>
            </label>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-14 text-lg font-bold uppercase tracking-wider rounded-full"
            >
              {loading ? (
                <span className="animate-pulse">Logging in...</span>
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Login
                </>
              )}
            </Button>

            {/* iter195 (Phase 3) — Passkey passwordless sign-in. */}
            {passkeyReady && (
              <button
                type="button"
                onClick={handlePasskeyLogin}
                disabled={passkeyBusy || loading}
                data-testid="passkey-login-btn"
                className="w-full h-12 rounded-full border-2 border-primary/40 bg-white dark:bg-card hover:bg-primary/10 active:scale-[0.99] transition flex items-center justify-center gap-2 font-bold text-sm uppercase tracking-wider disabled:opacity-60"
              >
                <Fingerprint className="w-5 h-5" />
                {passkeyBusy ? 'Verifying…' : 'Sign in with Face ID'}
              </button>
            )}
          </form>

          {/* Sign up link */}
          <p className="mt-8 text-center text-muted-foreground">
            Don't have an account?{' '}
            <Link 
              to="/signup" 
              data-testid="signup-link"
              className="text-primary font-semibold hover:underline"
            >
              Sign up
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Bottom branding */}
      <div className="py-3 flex items-center justify-center gap-2" data-testid="stancor-branding">
        <img
          src="/stancor-logo.png"
          alt="Stancor Group"
          className="h-8 w-auto rounded"
          data-testid="stancor-logo"
        />
        <p className="text-xs text-muted-foreground" data-testid="stancor-footer">
          Made by Stancor Group
        </p>
      </div>
      <div className="h-2 bg-primary" />
    </div>
  );
}
